<template>
    <v-app>
        <header-card-public current_page="landing"></header-card-public>
        <v-content class="bg-container fade">
            <v-layout fill-height row wrap>
                
                <!-- Left/top Side: Ads -->
                <v-flex v-if="!isMobile" xs12 sm6 class="ads-section">
                    <div class="ads-bg">
                        <public-notifications></public-notifications>
                    </div>
                </v-flex>

                <!-- Right/bottom Side: Welcome Card -->
                <v-flex xs12 sm6 :class="welcomeSectionClass">
                    <v-layout fill-height align-center justify-center>
                       
                        <v-card :class="isMobile ? 'welcome-card-mobile' : 'welcome-card'">
                            <v-card-text>
                                
                                <h3 class="subheading text-xs-center white--text mb-20" v-html="welcome"></h3>

                                <v-alert v-if="error" :value="true" color="warning" icon="warning" outline>
                                    {{errorMessage}}
                                </v-alert>
                                
                                <div v-if="pwaDownloading && !error" class="text-xs-center white--text mt-20">
                                    <h3>Checking System Updates...</h3>
                                    <v-progress-circular indeterminate color="primary" size="40" />
                                </div>

                                <v-btn v-if="canLoginWithDevice && !pwaDownloading" 
                                        class="no-uppercase" block dark
                                        :color="welcomeButtonColor" 
                                        :disabled="loading || pwaDownloading"
                                        @click="loginWithDevice" 
                                        title="Uses Face ID, fingerprint, or device PIN. Authentication stays securely on your device.">
                                    
                                    <!-- Dynamic button text for PWA downloading state -->
                                    <v-icon v-if="!loading" class="mr-5">fingerprint</v-icon>
                                    <span v-if="loading">Waiting for device authentication...</span>
                                    <span v-else>Sign in with Device</span>

                                </v-btn>

                                <!-- Condition updated: disabled when loading OR when service worker downloading -->
                                <v-btn 
                                    v-if="!pwaDownloading"
                                    class="no-uppercase white--text" 
                                    :color="welcomeButtonColor" 
                                    outline block dark 
                                    :disabled="loading || pwaDownloading"
                                    @click="gotoLoginByEmail" 
                                    title="Sign in using your email and password.">
                                    
                                    <v-icon class="mr-5">password</v-icon>
                                    <span>Sign in with Password</span>
                                </v-btn>
                            </v-card-text>
                        </v-card>

                        <div class="footer-section caption">
                            <div class="white--text text-xs-center">
                                &copy; Copyright 2024 SweldoNow OPC. All Rights Reserved.
                            </div>
                            <other-legal-docs></other-legal-docs>
                        </div>
                    </v-layout>
                </v-flex>
            </v-layout>
        </v-content>
    </v-app>
</template>

<script>

import '@/assets/css/public.css'
import '@/assets/css/season.css'
import publicPages from '@/helpers/public-pages.js'
import { isPlatformAuthenticatorAvailable } from "@/helpers/webauthn"
import { base64urlToBuffer, bufferToBase64url } from '@/helpers/webauthn-utils.js'
import { getUAData } from '@/helpers/user-agent.js'
import webauthnApi from '@/api/webauthn'
import auth from "@/auth/auth.js"
import { parseApiError, generateErrorHtmlReport } from '@/helpers/login-error-handler'
import emailApi from '@/api/email-sender'
import appsApi from '@/api/applications'

export default {
    name: 'Home',
    metaInfo: {
        title: 'Home',
        meta: [
            { charset: 'utf-8' },
            { name: 'description', content: '' },
            { name: 'viewport', content: 'width=device-width, initial-scale=1' },
        ],
    },
    components: {
        'header-card-public': () => import('@/components/Common/HeaderPublic'),
        'other-legal-docs': () => import('@/components/Common/OtherLegalDocs'),
        'public-notifications': () => import('@/components/PublicNotifications/PublicNotifications'),
    },
    data() {
        return {
            error: false,
            errorMessage: null,
            loading: false,
            disabled: false,
            deviceLoginSupported : false,
            userAgent: null,
            pwaDownloading: true,
        };
    },
    methods: {
        async getLatestUIVersions() {
            try {
                const response = await appsApi.getUIVersions()
                if (response && response.status == 200) {
                    const latestVersion = response.data.appUIVersion.toLowerCase().trim()
                    const localVersion = (process.env.VUE_APP_UI_VERSION || '').toLowerCase().trim()
                    
                    console.log('getLatestUIVersions')
                    console.log('latestVersion: ', latestVersion)
                    console.log('localVersion: ', localVersion)

                    // Toggle lock if versions do not match
                    this.pwaDownloading = (latestVersion !== localVersion) 
                }
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        async loginWithDevice() {
            try {
                this.loading = true

                // 1. Get Assertion Options (Challenge + Allowed Credentials)
                // Pass your encrypted UserId here
                const request = {
                    userId: this.encryptedUserId,
                    deviceFingerprint: this.userAgent.fingerprint
                }

                const startAssertionResponse = await webauthnApi.startAssertion(request)
                const options = (startAssertionResponse && startAssertionResponse.data) ? startAssertionResponse.data.options : null;

                // If the API failed to give options, stop early and throw a controlled error
                if (!options) {
                    throw new Error('Incomplete login response from server.')
                }
                
                // 2. Prepare for browser (Convert strings to Buffers)
                const publicKey = {
                    ...options,
                    challenge: base64urlToBuffer(options.challenge),
                    allowCredentials: options.allowCredentials.map(c => ({
                        ...c,
                        id: base64urlToBuffer(c.id)
                    }))
                };

                // 3. Trigger Browser Biometric Prompt
                // The phone/laptop will now show the FaceID/Fingerprint UI
                const assertion = await navigator.credentials.get({ publicKey })

                // 4. Format Response for Finish API
                const response = {
                    id: assertion.id,
                    rawId: bufferToBase64url(assertion.rawId),
                    type: assertion.type,
                    response: {
                        authenticatorData: bufferToBase64url(assertion.response.authenticatorData),
                        clientDataJSON: bufferToBase64url(assertion.response.clientDataJSON),
                        signature: bufferToBase64url(assertion.response.signature),
                        userHandle: assertion.response.userHandle ? bufferToBase64url(assertion.response.userHandle) : null
                    }
                }

                // 5. Send to Finish API to get your JWT/Session
                const finishAssertionResponse = await webauthnApi.finishAssertion(this.encryptedUserId, response)

                if (finishAssertionResponse && finishAssertionResponse.status == 200) {                    
                    this.loading = false

                    //save user state
                    auth.saveUserState(finishAssertionResponse, false)

                    // redirect to My Dashboard or target url (if any)
                    const destination = this.$route.query.redirect || '/'
                    this.$router.push(destination).catch(err => {
                        // Only ignore errors regarding Navigating to the same location
                        if (err.name !== 'NavigationDuplicated') {
                            console.error(err);
                        }
                    })
                } else {
                    throw new Error('Authentication verification failed.')
                }
            } catch(error) {
                this.error = true;
                this.loading = false;
                
                // 1. Hand off error details to the dedicated notification function
                this.sendErrorEmailNotification(error);

                // 2. Output safe UI error text to user interface
                this.errorMessage = parseApiError(error);
            }
        },
        async sendErrorEmailNotification(error) {
            try {
                // Compile the deep diagnostic HTML layout report
                const errorHtmlBody = generateErrorHtmlReport(error, {
                    userId: this.encryptedUserId,
                    fingerprint: (this.userAgent && this.userAgent.fingerprint) ? this.userAgent.fingerprint : null,
                    isDeviceLoginCredentialExists: this.isDeviceLoginCredentialExists,
                    deviceLoginSupported: this.deviceLoginSupported
                });

                // Map data properties to look exactly like AwsSesEmailRequest C# structure
                const errorName = (error && error.name) ? error.name : 'UnknownError';
                const emailRequestPayload = {
                    subject: `🚨 SweldoNow Login Failure Report: ${errorName}`,
                    htmlBody: errorHtmlBody,
                    from: {
                        name: "SweldoNow Client Core",
                        email: "no-reply@mail.sweldonowservices.net"
                    },
                    to: {
                        name: "SweldoNow Support",
                        email: "support@sweldonow.ph" 
                    },
                    copyUsers: [
                        {
                            name: "Rodel P. Ocfemia",
                            email: "rodel.p.ocfemia@gmail.com" 
                        }
                    ],
                    bccUsers: [],
                    attachments: []
                };

                // Non-blocking dispatch call to backend Email controller 
                await emailApi.sendErrorHandler(emailRequestPayload);

            } catch (deliveryError) {
                // Suppressed log so a failure in email mechanics never freezes the main login screen
                console.error("Critical: Could not transmit diagnostics over SES service", deliveryError);
            }
        },
        gotoLoginByEmail() {
            this.$router.push({
                path: '/login',
                query: { redirect: this.$route.query.redirect } // Pass it forward
            });
        },
    },
    async created() {
        await this.getLatestUIVersions()

        // device login 
        this.deviceLoginSupported = await isPlatformAuthenticatorAvailable()
        this.userAgent = await getUAData()

    },
    computed: {
        isDeviceLoginCredentialExists() {
            return this.$store.state.isDeviceLoginCredentialExists ? this.$store.state.isDeviceLoginCredentialExists : false
        },
        encryptedUserId() {
            return this.$store.state.userId
        },
        canLoginWithDevice() {
            return this.isDeviceLoginCredentialExists &&
                this.deviceLoginSupported &&
                this.encryptedUserId //use this encrypted UserId instead of email in WebAuthn/assertion/start api
        },
        //public pages
        seasonDetails() {
            return publicPages.getSeasonDetails(this.$route.query.action, this.isMobile);
        },
        welcome() {
            return this.seasonDetails.greeting;
        },
        welcomeSectionClass() {
            return this.seasonDetails.className;
        },
        welcomeButtonColor() {
            return this.seasonDetails.buttonColor;
        },
    }
};
</script>

<style scoped>
.welcome-card, .welcome-card-mobile {
    transition: all 0.5s ease;
    animation: fadeInUp 0.8s ease;
    height:auto;
}
@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}
</style>
